import { AxiosError } from 'axios'

import { api } from '~/utils/api'
import { storageAuthKey } from '~/utils/storageKeys'

import { insertToast } from '../toasts/services'
import { authenticationAtom } from './data'
import { AuthenticateAdminDTO, PersonalTrainerSignInDTO } from './schema'
import { Authentication } from './types'

const impersonationHeaderKey = 'x-personal-trainer-id'

function applyImpersonationHeader(personalTrainerId?: string) {
  if (personalTrainerId) {
    api.defaults.headers.common[impersonationHeaderKey] = personalTrainerId
  } else {
    delete api.defaults.headers.common[impersonationHeaderKey]
  }
}

export function signOut() {
  const role = authenticationAtom.get()?.role
  localStorage.clear()
  delete api.defaults.headers.authorization
  applyImpersonationHeader()
  authenticationAtom.set(undefined)
  window.location.replace(role === 'admin' ? '/admin' : '/')
}

export function initiateSession() {
  const session = authenticationAtom.get()
  if (session) {
    api.defaults.headers.authorization = `Bearer ${session.token}`
    applyImpersonationHeader(session.impersonation?.personalTrainerId)
  }
}

export function updateSessionUser(user: Authentication['user']) {
  const sessionString = localStorage.getItem(storageAuthKey)

  if (sessionString && user) {
    const session: Authentication = JSON.parse(sessionString)
    session.user = user
    localStorage.setItem(storageAuthKey, JSON.stringify(session))
    authenticationAtom.set(session)
  }
}

export async function authenticateAdmin(payload: AuthenticateAdminDTO) {
  await api
    .post<Authentication>('/admin/authenticate', payload)
    .then(({ data }) => {
      if (!data) return
      api.defaults.headers.authorization = `Bearer ${data.token}`
      applyImpersonationHeader()
      localStorage.setItem(storageAuthKey, JSON.stringify(data))
      authenticationAtom.set(data)
    })
    .catch((error: AxiosError<APIError>) => {
      if (error.response?.data.message) {
        insertToast({
          title: 'Falha ao autenticar',
          duration: 3,
          message: error.response.data.message,
          type: 'error',
        })
      }
    })
}

export async function authenticatePersonalTrainer(
  payload: PersonalTrainerSignInDTO,
) {
  await api
    .post<Authentication>('personal-trainers/authenticate', payload)
    .then(({ data }) => {
      if (!data) return
      api.defaults.headers.authorization = `Bearer ${data.token}`
      applyImpersonationHeader()
      localStorage.setItem(storageAuthKey, JSON.stringify(data))
      authenticationAtom.set(data)
    })
    .catch((error) => {
      console.log(error)
      insertToast({
        title: 'Falha ao autenticar',
        duration: 3,
        message: error.response.data.message,
        type: 'error',
      })
    })
}

export function setAdminImpersonation(
  personalTrainerId: string,
  personalTrainerName?: string,
) {
  const session = authenticationAtom.get()
  if (!session || session.role !== 'admin') return

  const impersonation = { personalTrainerId, personalTrainerName }
  const updatedSession: Authentication = {
    ...session,
    impersonation,
  }

  localStorage.setItem(storageAuthKey, JSON.stringify(updatedSession))
  authenticationAtom.set(updatedSession)
  applyImpersonationHeader(personalTrainerId)
}

export function clearAdminImpersonation() {
  const session = authenticationAtom.get()
  if (!session) return

  const updatedSession: Authentication = {
    ...session,
    impersonation: undefined,
  }

  localStorage.setItem(storageAuthKey, JSON.stringify(updatedSession))
  authenticationAtom.set(updatedSession)
  applyImpersonationHeader()
}
